import React from 'react';
import type { CompanyProfileWidgetInstanceConfig, CompanyProfileData } from './types';
import { WidgetLoadingState, WidgetErrorState, WidgetPreviewBanner, formatPrice, formatMultiple } from '../../../design';
import { KeyMetricBadge } from '../../../blocks/chart-widget';
import { useDataProviderWidget } from '../../../blocks/hooks';

// Sample data shown during dashboard construction (no asset assigned)
const SAMPLE_DATA: CompanyProfileData = {
  ticker: 'SAMPLE',
  company_name: 'Sample Corporation',
  sector: 'Technology',
  industry: 'Software',
  fy_ending: 'December',
  currency: 'USD',
  market_cap: 2850000000000,
  pe_ratio: 28.5,
};

interface Props {
  config: CompanyProfileWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: unknown) => void;
  readOnly?: boolean;
  widgetInstanceId?: string;
}

const CompanyProfileWidgetRenderer: React.FC<Props> = ({
  config,
  dashboardData = {},
  widgetInstanceId,
}) => {
  const uid = dashboardData?.assetDataUid as string | undefined;
  const isPreview = !uid;

  const { data, loading, error, refetch } = useDataProviderWidget<CompanyProfileData>(
    'company_profile',
    uid,
    config as unknown as Record<string, unknown>,
    { enabled: !!uid, widgetInstanceId },
  );

  const displayData = uid ? data : SAMPLE_DATA;

  if (loading) return <WidgetLoadingState size="sm" />;
  if (error) return <WidgetErrorState message={error} onRetry={refetch} />;
  if (!displayData) return null;

  // Company name + ticker live in the header; the grid carries classification
  // and fundamentals so nothing is duplicated.
  const details: Array<{ label: string; value: string }> = [
    { label: 'Sector', value: displayData.sector ?? '--' },
    { label: 'Industry', value: displayData.industry ?? '--' },
    { label: 'FY Ending', value: displayData.fy_ending ?? '--' },
    { label: 'Currency', value: displayData.currency ?? '--' },
    { label: 'Market Cap', value: formatPrice(displayData.market_cap) },
    { label: 'P/E Ratio', value: formatMultiple(displayData.pe_ratio) },
  ];

  return (
    <div className={`h-full flex flex-col overflow-hidden rounded-xl border border-neutral-200 bg-surface shadow-sm ${isPreview ? 'opacity-70' : ''}`}>
      {isPreview && <WidgetPreviewBanner />}

      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-neutral-100 shrink-0">
        <div className="min-w-0 flex-1 truncate" title={displayData.company_name}>
          <span className="text-sm font-semibold text-neutral-800">{displayData.company_name}</span>
          <span className="text-xs text-neutral-500 ml-2">{displayData.ticker}</span>
        </div>
        {displayData.market_cap != null && (
          <KeyMetricBadge label="Mkt Cap" value={formatPrice(displayData.market_cap)} />
        )}
      </div>

      {/* Detail grid — vertically centered + tight spacing so all fields fit
          the tile without a scrollbar. */}
      <div className="flex-1 overflow-auto flex flex-col justify-center">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 px-3 py-2">
          {details.map(item => (
            <div key={item.label} className="min-w-0">
              <dt className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">{item.label}</dt>
              <dd className="text-sm text-neutral-800 truncate" title={item.value}>{item.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
};

export default CompanyProfileWidgetRenderer;
